import React, { useState } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Tabs,
  Timeline,
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Empty,
  Popconfirm,
  message,
  Divider,
} from "antd";
import {
  DeploymentUnitOutlined,
  HistoryOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  MedicineBoxOutlined,
  ToolOutlined,
  DollarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ShopOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { TTraceableInstance } from "../../../models/traceabilityModel";
import CreateTraceableLogModal from "./CreateTraceableLogModal";
import traceabilityService from "../../../services/traceabilityService";

const { Title, Text, Paragraph } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  instance: TTraceableInstance | null;
  tokenData: any;
  companyUid: string;
  logTypes: any[];
  onRefresh: () => void;
  onEdit: (instance: TTraceableInstance) => void;
}

export const ViewTraceableInstanceDetailModal: React.FC<Props> = ({
  visible,
  onClose,
  instance,
  tokenData,
  companyUid,
  logTypes,
  onRefresh,
  onEdit,
}) => {
  const [logModalVisible, setLogModalVisible] = useState(false);

  if (!instance) return null;

  const getStatusTag = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <Tag color="green">Activo / Disponible</Tag>;
      case "IN_PRODUCTION":
        return <Tag color="blue">En Producción / Ciclo</Tag>;
      case "IN_USE":
        return <Tag color="cyan">En Uso / Asignado</Tag>;
      case "MAINTENANCE":
        return <Tag color="orange">En Mantenimiento</Tag>;
      case "SOLD":
        return <Tag color="purple">Vendido</Tag>;
      case "DISPOSED":
      case "RETIRED":
        return <Tag color="default">Retirado / Baja</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getLogIcon = (typeKey: string | undefined) => {
    switch (typeKey) {
      case "MEASUREMENT":
        return <ExperimentOutlined style={{ color: "#1890ff", fontSize: 16 }} />;
      case "VACCINATION":
      case "TREATMENT":
        return <MedicineBoxOutlined style={{ color: "#52c41a", fontSize: 16 }} />;
      case "MAINTENANCE":
        return <ToolOutlined style={{ color: "#fa8c16", fontSize: 16 }} />;
      case "DEPRECIATION":
        return <DollarOutlined style={{ color: "#722ed1", fontSize: 16 }} />;
      case "ASSIGNMENT":
        return <UserOutlined style={{ color: "#13c2c2", fontSize: 16 }} />;
      case "STATUS_CHANGE":
        return <CheckCircleOutlined style={{ color: "#eb2f96", fontSize: 16 }} />;
      default:
        return <ClockCircleOutlined style={{ color: "#8c8c8c", fontSize: 16 }} />;
    }
  };

  const handleRetire = async () => {
    try {
      const res = await traceabilityService.deleteInstance(tokenData, {
        id: instance.id,
        exit_reason: "Dado de baja desde ficha de detalle",
      });
      if (res.error) throw new Error(res.message);
      message.success("Elemento trazable retirado");
      onRefresh();
      onClose();
    } catch (err: any) {
      message.error(err.message || "Error al retirar elemento");
    }
  };

  const logs = instance.traceable_instance_logs || [];
  const metadata = instance.metadata || {};
  const activeGroups = instance.traceable_instance_group_members?.filter((m) => m.is_active) || [];

  return (
    <>
      <Drawer
        open={visible}
        onClose={onClose}
        width={720}
        title={
          <Space align="center">
            <DeploymentUnitOutlined style={{ color: "#1890ff", fontSize: 22 }} />
            <div>
              <Title level={5} style={{ margin: 0 }}>
                {instance.identifier} {instance.name ? `— ${instance.name}` : ""}
              </Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {instance.module_options?.string_value || "Elemento Trazable"} • Creado el{" "}
                {dayjs(instance.created_at).format("DD/MM/YYYY")}
              </Text>
            </div>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setLogModalVisible(true)}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
            >
              Registrar Evento
            </Button>
            <Button icon={<EditOutlined />} onClick={() => onEdit(instance)}>
              Editar
            </Button>
            {instance.status !== "RETIRED" && instance.status !== "SOLD" && (
              <Popconfirm
                title="¿Dar de baja este elemento?"
                description="El elemento pasará a estado RETIRADO manteniendo todo su historial."
                onConfirm={handleRetire}
                okText="Sí, dar de baja"
                cancelText="Cancelar"
              >
                <Button danger icon={<DeleteOutlined />}>
                  Dar de Baja
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        {/* Quick Header Summary */}
        <Card size="small" style={{ marginBottom: 16, backgroundColor: "#fafafa" }}>
          <Row gutter={16} align="middle">
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                ESTADO
              </Text>
              {getStatusTag(instance.status)}
            </Col>
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                PRODUCTO BASE
              </Text>
              <Text strong>{instance.storage_elements?.name || "Sin vincular"}</Text>
            </Col>
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                ALMACÉN
              </Text>
              <Text>
                <ShopOutlined /> {instance.storehouses?.name || "N/A"}
              </Text>
            </Col>
            <Col xs={12} sm={6}>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                TOTAL EVENTOS
              </Text>
              <Text strong style={{ color: "#1890ff", fontSize: 16 }}>
                {logs.length}
              </Text>
            </Col>
          </Row>
        </Card>

        <Tabs
          defaultActiveKey="history"
          items={[
            {
              key: "history",
              label: (
                <span>
                  <HistoryOutlined /> Historial y Eventos ({logs.length})
                </span>
              ),
              children: (
                <div style={{ padding: "8px 0" }}>
                  {logs.length === 0 ? (
                    <Empty
                      description="No hay eventos registrados en la línea de tiempo de este elemento"
                      style={{ margin: "40px 0" }}
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setLogModalVisible(true)}
                      >
                        Registrar Primer Evento
                      </Button>
                    </Empty>
                  ) : (
                    <Timeline
                      mode="left"
                      items={logs.map((log) => {
                        const typeKey = log.module_options?.key;
                        const metrics = log.metrics || {};
                        return {
                          key: log.id,
                          dot: getLogIcon(typeKey),
                          label: (
                            <div style={{ textAlign: "right", minWidth: 85 }}>
                              <Text strong style={{ display: "block" }}>
                                {dayjs(log.log_date).format("DD/MM/YYYY")}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {dayjs(log.log_date).format("HH:mm")}
                              </Text>
                            </div>
                          ),
                          children: (
                            <Card size="small" style={{ marginBottom: 8, borderColor: "#e8e8e8", borderRadius: 6 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                  marginBottom: 4,
                                }}
                              >
                                <Space wrap align="center" size={6} style={{ flex: "1 1 auto", minWidth: 150 }}>
                                  <Tag color="blue" style={{ margin: 0 }}>
                                    {log.module_options?.string_value || "Evento"}
                                  </Tag>
                                  {log.reference_type && log.reference_type.includes("CYCLE") && (
                                    <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>
                                      🌱 Ciclo #{log.reference_id}
                                    </Tag>
                                  )}
                                  <Text strong style={{ fontSize: 14 }}>
                                    {log.title || "Registro"}
                                  </Text>
                                </Space>
                                {log.new_status && (
                                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                      Cambió a:
                                    </Text>
                                    {getStatusTag(log.new_status)}
                                  </div>
                                )}
                              </div>

                              {log.notes && (
                                <Paragraph style={{ margin: "8px 0 4px", color: "#595959", fontSize: 13 }}>
                                  {log.notes}
                                </Paragraph>
                              )}

                              {/* Metrics summary */}
                              {Object.keys(metrics).length > 0 && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    padding: "6px 10px",
                                    backgroundColor: "#f9f9f9",
                                    borderRadius: 4,
                                    fontSize: 12,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "4px",
                                  }}
                                >
                                  {Object.entries(metrics).map(([key, val]) => (
                                    <Tag key={key} style={{ margin: 0 }}>
                                      <Text strong>{key}:</Text>{" "}
                                      {typeof val === "number" ? val.toLocaleString() : String(val)}
                                    </Tag>
                                  ))}
                                </div>
                              )}

                              {log.users && (
                                <div style={{ marginTop: 6 }}>
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    Registrado por: {log.users.name}
                                  </Text>
                                </div>
                              )}
                            </Card>
                          ),
                        };
                      })}
                    />
                  )}
                </div>
              ),
            },
            {
              key: "technical_sheet",
              label: (
                <span>
                  <FileTextOutlined /> Ficha Técnica
                </span>
              ),
              children: (
                <div>
                  <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Identificador Principal">{instance.identifier}</Descriptions.Item>
                    <Descriptions.Item label="Nombre / Alias">{instance.name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Categoría">
                      {instance.module_options?.string_value || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Producto Padre">
                      {instance.storage_elements?.name ? (
                        <span>
                          {instance.storage_elements.name}{" "}
                          {instance.storage_elements.sku ? `(${instance.storage_elements.sku})` : ""}
                        </span>
                      ) : (
                        "No asociado"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bodega / Almacén">
                      {instance.storehouses?.name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ubicación Física">{instance.location || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Fecha de Ingreso">
                      {instance.entry_date ? dayjs(instance.entry_date).format("DD/MM/YYYY") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Fecha de Salida / Baja">
                      {instance.exit_date ? dayjs(instance.exit_date).format("DD/MM/YYYY") : "-"}
                    </Descriptions.Item>
                    {instance.exit_reason && (
                      <Descriptions.Item label="Motivo de Salida" span={2}>
                        {instance.exit_reason}
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  <Divider orientation="left">Grupos Asignados</Divider>
                  {activeGroups.length === 0 ? (
                    <Text type="secondary">No pertenece a ningún grupo actualmente.</Text>
                  ) : (
                    <Space wrap>
                      {activeGroups.map((g) => (
                        <Tag key={g.id} color="cyan" style={{ fontSize: 13, padding: "4px 10px" }}>
                          {g.traceable_instance_groups?.name || `Grupo #${g.group_id}`}
                        </Tag>
                      ))}
                    </Space>
                  )}

                  {Object.keys(metadata).length > 0 && (
                    <>
                      <Divider orientation="left">Atributos y Metadatos Específicos</Divider>
                      <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
                        {Object.entries(metadata).map(([k, v]) => (
                          <Descriptions.Item key={k} label={k}>
                            {typeof v === "number" ? v.toLocaleString() : String(v)}
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    </>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Drawer>

      <CreateTraceableLogModal
        visible={logModalVisible}
        onClose={() => setLogModalVisible(false)}
        onSuccess={() => {
          onRefresh();
        }}
        tokenData={tokenData}
        companyUid={companyUid}
        instance={instance}
        logTypes={logTypes}
      />
    </>
  );
};

export default ViewTraceableInstanceDetailModal;
